import {
  Timestamp,
  getCountFromServer,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  type CollectionReference,
  type FieldValue,
  type FirestoreError,
  type QueryDocumentSnapshot,
} from "firebase/firestore";
import type { SnapshotMetadata } from "@/types/firestore/metadata";

/**
 * Stamp a write with the server time. Every write to a delta-synced
 * collection must do this (enforced by security rules), or other devices
 * never pick the change up.
 */
export function withUpdatedAt<T extends object>(
  data: T,
): T & { updated_at: FieldValue } {
  return { ...data, updated_at: serverTimestamp() };
}

/** Every document in the collection, as returned by a full sync. */
export type FullSyncSnapshot<T> = {
  documents: T[];
  /** Newest committed server `updated_at` among the documents, if any carry one. */
  latestUpdatedAt: Date | null;
  metadata: SnapshotMetadata;
};

/** Documents changed since the cursor, as returned by a delta listener. */
export type DeltaSyncSnapshot<T> = {
  changed: T[];
  latestUpdatedAt: Date | null;
  metadata: SnapshotMetadata;
};

type SyncCallbacks<S> = {
  onSnapshot: (snapshot: S) => void;
  onError: (error: FirestoreError) => void;
};

/**
 * Read `updated_at` straight from the raw snapshot so this works regardless of
 * whether the shared converter maps the field. Documents with pending local
 * writes only carry an estimated time and are skipped.
 */
function latestServerUpdatedAt<T>(
  docs: readonly QueryDocumentSnapshot<T>[],
): Date | null {
  let latest: number | null = null;
  for (const doc of docs) {
    if (doc.metadata.hasPendingWrites) continue;
    const value: unknown = doc.get("updated_at");
    if (value instanceof Timestamp) {
      latest = Math.max(latest ?? 0, value.toMillis());
    }
  }
  return latest === null ? null : new Date(latest);
}

/**
 * Listen to every document in a collection, ordered by timestamp descending.
 * Used for the initial (or recovery) full sync; callers unsubscribe once the
 * first server snapshot has arrived and switch to a delta listener.
 */
export function subscribeToAllDocuments<T>(
  ref: CollectionReference<T>,
  callbacks: SyncCallbacks<FullSyncSnapshot<T>>,
): () => void {
  return onSnapshot(
    query(ref, orderBy("timestamp", "desc")),
    { includeMetadataChanges: true },
    (snapshot) => {
      callbacks.onSnapshot({
        documents: snapshot.docs.map((doc) => doc.data()),
        latestUpdatedAt: latestServerUpdatedAt(snapshot.docs),
        metadata: {
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
        },
      });
    },
    callbacks.onError,
  );
}

/**
 * Listen to documents whose `updated_at` is newer than the cursor.
 * Documents without `updated_at` (never written since delta sync shipped) are
 * excluded by Firestore, which is fine: the full sync already delivered them.
 *
 * "removed" changes are ignored: besides deletes, Firestore reports one when a
 * document from this device gets a pending `serverTimestamp()` for
 * `updated_at`, because a pending timestamp never matches `>` in the local
 * cache. Deletes are caught by the count check instead.
 */
export function subscribeToDocumentChanges<T>(
  ref: CollectionReference<T>,
  cursor: number,
  callbacks: SyncCallbacks<DeltaSyncSnapshot<T>>,
): () => void {
  return onSnapshot(
    query(ref, where("updated_at", ">", Timestamp.fromMillis(cursor))),
    { includeMetadataChanges: true },
    (snapshot) => {
      const changed = snapshot
        .docChanges()
        .filter((change) => change.type !== "removed")
        .map((change) => change.doc.data());

      callbacks.onSnapshot({
        changed,
        latestUpdatedAt: latestServerUpdatedAt(snapshot.docs),
        metadata: {
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
        },
      });
    },
    callbacks.onError,
  );
}

/**
 * Exact number of documents in the collection, counted on the server.
 * Billed as one read per 1000 documents counted.
 */
export async function countDocuments<T>(
  ref: CollectionReference<T>,
): Promise<number> {
  const snapshot = await getCountFromServer(ref);
  return snapshot.data().count;
}
